import type { BreadcrumbsConfig } from "../core/types.js";
import { parseProviderConfigs } from "./provider.js";
import type { ProviderConfig } from "./provider.js";
import type { TelemetryRuntimeInput } from "./runtime.js";
import {
  assertBoolean,
  assertNonEmptyString,
  assertOptionalString,
  assertSampleRate,
  parseRuntimeInput,
  schemaError,
} from "./validate.js";

export interface TelemetryContextConfig {
  userId?: string;
  sessionId?: string;
  route?: string;
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
}

export interface TelemetryConfig {
  appId: string;
  runtime: TelemetryRuntimeInput;
  environment?: "development" | "staging" | "production";
  release?: string;
  sampleRate?: number;
  captureGlobalErrors?: boolean;
  breadcrumbs?: BreadcrumbsConfig;
  providers: ProviderConfig[];
  context?: TelemetryContextConfig;
}

export type TelemetryConfigInput = {
  appId: string;
  runtime?: TelemetryRuntimeInput;
  environment?: TelemetryConfig["environment"];
  release?: string;
  sampleRate?: number;
  captureGlobalErrors?: boolean;
  breadcrumbs?: BreadcrumbsConfig;
  providers?: ProviderConfig[];
  context?: TelemetryContextConfig;
};

const ENVIRONMENTS = new Set(["development", "staging", "production"]);

function parseBreadcrumbs(value: unknown): BreadcrumbsConfig | undefined {
  if (value === undefined) return undefined;
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw schemaError("breadcrumbs must be an object");
  }
  const raw = value as Record<string, unknown>;
  const maxSize =
    raw.maxSize === undefined
      ? undefined
      : typeof raw.maxSize === "number" && raw.maxSize >= 1
        ? Math.floor(raw.maxSize)
        : (() => {
            throw schemaError("breadcrumbs.maxSize must be a number >= 1");
          })();
  return {
    enabled:
      raw.enabled === undefined
        ? undefined
        : assertBoolean(raw.enabled, "breadcrumbs.enabled"),
    maxSize,
  };
}

function parseContext(value: unknown): TelemetryContextConfig | undefined {
  if (value === undefined) return undefined;
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw schemaError("context must be an object");
  }
  const raw = value as Record<string, unknown>;
  return {
    userId: assertOptionalString(raw.userId, "context.userId"),
    sessionId: assertOptionalString(raw.sessionId, "context.sessionId"),
    route: assertOptionalString(raw.route, "context.route"),
    tags: raw.tags as Record<string, string> | undefined,
    extra: raw.extra as Record<string, unknown> | undefined,
  };
}

export function parseTelemetryConfig(input: TelemetryConfigInput): TelemetryConfig {
  const environment = input.environment;
  if (environment !== undefined && !ENVIRONMENTS.has(environment)) {
    throw schemaError('environment must be "development", "staging", or "production"');
  }

  return {
    appId: assertNonEmptyString(input.appId, "appId"),
    runtime: parseRuntimeInput(input.runtime),
    environment,
    release: assertOptionalString(input.release, "release"),
    sampleRate: assertSampleRate(input.sampleRate, "sampleRate"),
    captureGlobalErrors: assertBoolean(input.captureGlobalErrors, "captureGlobalErrors"),
    breadcrumbs: parseBreadcrumbs(input.breadcrumbs),
    providers: parseProviderConfigs(input.providers),
    context: parseContext(input.context),
  };
}
